import React, { useState, useEffect } from 'react';
import {
  CssBaseline,
  Container,
  Typography,
  Box,
  Button,
  Paper,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation
} from 'react-router-dom';
import TemplateComparison from './pages/TemplateComparison';
import StandardComparison from './pages/StandardComparison';
import CustomComparison from './pages/CustomComparison';

import SingleSpreadsheetAnalysis from './pages/SingleSpreadsheetAnalysis';
import Login from './pages/Login';

const AppContent = () => {
  const location = useLocation();
  const isOnHomePage = location.pathname === '/';

  return (
    <Container maxWidth="lg" sx={{ textAlign: 'center', paddingTop: '50px' }}>
      {/* Only show this section on the home page */}
      {isOnHomePage && (
        <Box sx={{ backgroundColor: '#183B4E', color: 'white', padding: '20px', borderRadius: '8px' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
            Welcome to Spreadsheet Comparator
          </Typography>
          <Typography variant="h6" sx={{ fontStyle: 'italic', marginBottom: '40px' }}>
            Powered by Kava CRM
          </Typography>
        </Box>
      )}

      {/* Home Page Options Section */}
      {isOnHomePage && (
        <Paper elevation={6} sx={{ padding: '30px', borderRadius: '8px', marginTop: '30px' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
            Choose an Option:
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Link to="/standard-comparison" style={{ textDecoration: 'none' }}>
              <Button variant="contained" color="secondary" sx={{ padding: '15px', minWidth: '200px' }}>
                Standard Comparison
              </Button>
            </Link>
            <Link to="/custom-comparison" style={{ textDecoration: 'none' }}>
              <Button variant="contained" sx={{ padding: '15px', minWidth: '200px' }}>
                Custom Comparison
              </Button>
            </Link>
          </Box>
        </Paper>
      )}

      {/* Define Routes */}
      <Routes>
        <Route path="/template-comparison" element={<TemplateComparison />} />
        <Route path="/standard-comparison" element={<StandardComparison />} />
        <Route path="/custom-comparison" element={<CustomComparison />} />
        <Route path="/single-analysis" element={<SingleSpreadsheetAnalysis />} />
      </Routes>
    </Container>
  );
};

const GITHUB_API_BASE = 'https://api.github.com/users/';
const VALID_USERNAME_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?$/;

const App = () => {
  const [user, setUser]           = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userParam = urlParams.get('user');
    const finalUser = userParam || sessionStorage.getItem('user');

    if (finalUser && VALID_USERNAME_PATTERN.test(finalUser)) {
      setUser(finalUser);
      sessionStorage.setItem('user', finalUser);
      window.history.replaceState({}, document.title, window.location.pathname);

      // Fetch avatar
      fetch(GITHUB_API_BASE + encodeURIComponent(finalUser))
          .then((res) => res.json())
          .then((data) => {
            if (data && data.avatar_url && typeof data.avatar_url === 'string' && data.avatar_url.startsWith('https://')) {
              setAvatarUrl(data.avatar_url);
            }
          })
          .catch(console.error);
    }
  }, []);

  if (!user) {
    return (
      <div>
        <CssBaseline />
        <Container maxWidth="sm" sx={{ textAlign: 'center', paddingTop: '50px' }}>
          <Box sx={{ backgroundColor: '#183B4E', color: 'white', padding: '30px', borderRadius: '8px' }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
              SPREADSHEET COMPARATOR
            </Typography>
            <Typography variant="h6" sx={{ fontStyle: 'italic', marginBottom: '40px' }}>
              Powered by Kava CRM
            </Typography>
          </Box>

          <Paper elevation={6} sx={{ padding: '30px', borderRadius: '8px', marginTop: '30px' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
              Login to get started
            </Typography>
            <Login />
          </Paper>
        </Container>
      </div>
    );
  }

  return (
      <Router>
        <CssBaseline />

        <AppBar position="fixed" sx={{ backgroundColor: '#183B4E' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {user}
            </Typography>
            {avatarUrl && (
                <img
                    src={avatarUrl}
                    alt={user || ''}
                    style={{ width: 36, height: 36, borderRadius: '50%' }}
                />
            )}
          </Toolbar>
        </AppBar>

        {/* Push main content below AppBar */}
        <Box sx={{ paddingTop: '80px' }}>
          <AppContent />
        </Box>
      </Router>
  );
};

export default App;
